import type { CSSProperties, ReactNode } from "react"
import { useMemo, useState } from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

import { cn } from "@productlane/lib/src/cn"
import { PortalSectionHeader } from "@productlane/ui"

import type { RoadmapDragGroup, RoadmapRowType } from "./reorder"
import type { RoadmapStatusGroup } from "./status-groups"
import { ProjectOrIssueListSection } from "@/components/issue-or-project-items/project-or-issue-list-section"
import {
	GRIP_CLASS,
	GripGlyph,
	HELD_CLASS,
} from "@/components/issue-or-project-items/sortable-list-item"
import { projectStatusMap } from "@/screens/app/projects/project-status-select"
import { GRIP_ATTRIBUTE, roadmapListId, sortableGroupId } from "./reorder"

const projectStateFor = (label: string, fallback?: string | null) =>
	Object.entries(projectStatusMap).find(([, name]) => name === label)?.[0] ??
	fallback ??
	"backlog"

interface StatusGroupProps {
	group: RoadmapStatusGroup
	maxMapProjects: Map<string, number>
	isPending: boolean
	renderTrailingAction?: (
		type: "project" | "issue",
		data: { id: string },
	) => ReactNode
}

export function SortableStatusGroup({
	groupKey,
	groupDragActive,
	...props
}: StatusGroupProps & { groupKey: string; groupDragActive: boolean }) {
	const data = useMemo<RoadmapDragGroup>(
		() => ({ kind: "group", groupKey, label: props.group.label }),
		[groupKey, props.group.label],
	)
	const id = sortableGroupId(props.group.label)
	const {
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id, data })

	return (
		<div data-reorder-id={id} className={cn(isDragging && "relative z-30")}>
			<StatusGroup
				{...props}
				reorder={{
					// A span, not a button: it sits inside the header's toggle button.
					grip: (
						<span
							ref={setActivatorNodeRef}
							aria-hidden
							{...{ [GRIP_ATTRIBUTE]: "" }}
							className={cn(
								GRIP_CLASS,
								// Takes the status icon's exact box on hover, so nothing shifts. z-10:
								// the header's content box is positioned and would cover the grip.
								"absolute left-3 top-1/2 z-10 size-3.5 -translate-y-1/2 group-hover/header:opacity-100",
								isDragging && "opacity-100",
							)}
							{...listeners}
							onClick={(event) => {
								event.preventDefault()
								event.stopPropagation()
							}}
						>
							<GripGlyph />
						</span>
					),
					// Rows hide while a group moves, so every header is one row tall.
					forceCollapsed: groupDragActive,
					isHeld: isDragging,
					// The header is what moves: dnd-kit pins the node's top under the
					// cursor, and a long group's own top can be far off-screen.
					headerRef: setNodeRef,
					headerStyle: {
						transform: CSS.Translate.toString(transform),
						transition,
					},
				}}
			/>
		</div>
	)
}

export function StatusGroup({
	group,
	maxMapProjects,
	isPending,
	renderTrailingAction,
	reorder,
}: StatusGroupProps & {
	reorder?: {
		grip: ReactNode
		forceCollapsed: boolean
		isHeld: boolean
		headerRef: (element: HTMLElement | null) => void
		headerStyle: CSSProperties
	}
}) {
	const [isCollapsed, setIsCollapsed] = useState(false)
	const sortableListId = (rowType: RoadmapRowType) =>
		reorder
			? (roadmapId: string | null) =>
					roadmapListId(rowType, group.label, roadmapId)
			: undefined

	const firstIssue = group.issues[0]
	const firstProject = group.projects[0]
	// Issues carry Linear's own status color, so they win the icon when a group
	// holds both. Pending projects stay iconless, as they have no portal state.
	const icon = firstIssue
		? {
				statusType: "issue" as const,
				status: firstIssue.status,
				statusColor: firstIssue.statusColor ?? undefined,
			}
		: !isPending && firstProject
			? {
					statusType: "project" as const,
					status: projectStateFor(group.label, firstProject.state),
					statusColor: firstProject.stateColor ?? undefined,
				}
			: {}

	return (
		<div>
			<PortalSectionHeader
				className={cn(
					"group/header mx-3 w-[calc(100%-1.5rem)] rounded-lg max-md:mx-3 max-md:px-3",
					reorder?.isHeld && HELD_CLASS,
				)}
				statusType={icon.statusType}
				status={icon.status}
				statusColor={icon.statusColor}
				count={group.projects.length + group.issues.length}
				collapsible
				collapsed={isCollapsed}
				onToggle={() => setIsCollapsed((value) => !value)}
				leading={reorder?.grip}
				headerRef={reorder?.headerRef}
				style={reorder?.headerStyle}
				iconSlotClassName={
					reorder
						? cn("group-hover/header:opacity-0", reorder.isHeld && "opacity-0")
						: undefined
				}
			>
				{group.label}
			</PortalSectionHeader>

			<div hidden={isCollapsed || reorder?.forceCollapsed}>
				{group.projects.length > 0 && (
					<ProjectOrIssueListSection
						type="project"
						data={group.projects}
						maxMap={maxMapProjects}
						status={group.label}
						isPending={isPending}
						renderTrailingAction={renderTrailingAction}
						sortableListId={sortableListId("project")}
					/>
				)}
				{group.issues.length > 0 && (
					<ProjectOrIssueListSection
						type="issue"
						data={group.issues}
						maxMap={null}
						status={group.label}
						isPending={isPending}
						renderTrailingAction={renderTrailingAction}
						sortableListId={sortableListId("issue")}
					/>
				)}
			</div>
		</div>
	)
}
